import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { AdvancedResultsGrid } from './AdvancedResultsGrid';
import { AdvancedHuntingExplorer, type AdvancedHuntingHistoryItem } from './AdvancedHuntingExplorer';
import { KqlIntellisenseEditor } from './intelligence/KqlIntellisenseEditor';
import { KustoForgeBuilder } from './intelligence/KustoForgeBuilder';
import type { KqlTableDef } from '../lib/kqlCatalog';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';


interface AdvancedHuntingTabProps {
  fixedCustomerId?: string;
  hideCustomerSelector?: boolean;
  featureFlags?: Record<string, boolean>;
  /** Optional KQL supplied by another module, for example Incident Evidence pivots. */
  pendingQuery?: string | null;
  onPendingQueryConsumed?: () => void;
}

type HuntingSource = 'sentinel' | 'defender';
type TimeRangeMode = 'lookback' | 'absolute' | 'in_query';
type QueryExecutionMode = 'standard' | 'set_query_now' | 'custom';
type LookbackUnit = 'hours' | 'days';
type SentinelTimeMode = TimeRangeMode;
type SentinelLookbackUnit = LookbackUnit;

interface AdvancedHuntingResponse {
  ok: boolean;
  source?: HuntingSource;
  customerId: string;
  workspaceId?: string;
  tenantId?: string;
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  durationMs: number;
  appliedLimit?: number;
  timespan?: string;
  error?: string;
}

interface AiQueryResponse {
  ok: boolean;
  source: HuntingSource;
  query: string;
  explanation: string;
  tables: string[];
  assumptions: string[];
  safetyNotes: string[];
  recommendedTimespan?: string;
  model?: string;
  generatedAt?: string;
  error?: string;
}

function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function csvEscape(value: unknown): string {
  const raw = stringifyCell(value)
    .replace(/\r?\n|\r/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (/[",\n\r]/.test(raw)) {
    return `"${raw.replace(/"/g, '""')}"`;
  }

  return raw;
}

function safeFilenamePart(value: string): string {
  return value
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function exportAdvancedHuntingCsv(result: AdvancedHuntingResponse, customerName?: string) {
  const headers = result.columns;

  const csvRows = [
    headers.map(csvEscape).join(','),
    ...result.rows.map((row) =>
      headers.map((_column, index) => csvEscape(row[index])).join(',')
    ),
  ];

  const csvContent = `\uFEFF${csvRows.join('\r\n')}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const customerPart = safeFilenamePart(customerName || result.customerId || 'workspace');
  const sourcePart = result.source === 'defender' ? 'defender-xdr' : 'sentinel-log-analytics';
  const filename = `advanced-hunting-${sourcePart}-${customerPart}-${timestamp}.csv`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const SENTINEL_SAMPLE_QUERIES = [
  {
    label: 'Recent incidents',
    query: `SecurityIncident
| order by TimeGenerated desc
| take 20`,
  },
  {
    label: 'Incidents by severity',
    query: `SecurityIncident
| summarize Count=count() by Severity
| order by Count desc`,
  },
  {
    label: 'Recent alerts',
    query: `SecurityAlert
| order by TimeGenerated desc
| take 20`,
  },
  {
    label: 'Threat Intel indicators',
    query: `union isfuzzy=true ThreatIntelIndicators, ThreatIntelligenceIndicator
| take 20`,
  },
  {
    label: 'Data ingestion by table',
    query: `Usage
| summarize GB=sum(Quantity) / 1000 by DataType
| top 20 by GB desc`,
  },
];

const DEFENDER_SAMPLE_QUERIES = [
  {
    label: 'Recent device events',
    query: `DeviceInfo
| summarize arg_max(Timestamp, *) by DeviceId
| project Timestamp, DeviceName, OSPlatform, LoggedOnUsers, ExposureLevel
| order by Timestamp desc
| take 20`,
  },
  {
    label: 'Suspicious PowerShell',
    query: `DeviceProcessEvents
| where Timestamp > ago(7d)
| where FileName =~ "powershell.exe"
| project Timestamp, DeviceName, AccountName, FileName, ProcessCommandLine, InitiatingProcessFileName
| order by Timestamp desc
| take 50`,
  },
  {
    label: 'Network connections',
    query: `DeviceNetworkEvents
| where Timestamp > ago(24h)
| project Timestamp, DeviceName, InitiatingProcessFileName, RemoteUrl, RemoteIP, RemotePort, ActionType
| order by Timestamp desc
| take 50`,
  },
  {
    label: 'Email URL clicks',
    query: `UrlClickEvents
| where Timestamp > ago(7d)
| project Timestamp, AccountUpn, Url, ActionType, Workload, NetworkMessageId
| order by Timestamp desc
| take 50`,
  },
  {
    label: 'Alert evidence',
    query: `AlertInfo
| where Timestamp > ago(7d)
| join kind=leftouter AlertEvidence on AlertId
| project Timestamp, Title, Severity, ServiceSource, EntityType, EvidenceRole, DeviceName, AccountName, RemoteUrl
| order by Timestamp desc
| take 50`,
  },
];

const SOURCE_META: Record<HuntingSource, { label: string; subtitle: string; help: string }> = {
  sentinel: {
    label: 'Sentinel / Log Analytics',
    subtitle: 'Workspace KQL',
    help: 'Queries the selected customer Log Analytics workspace and keeps the KQL Table Access Master allowlist in force.',
  },
  defender: {
    label: 'Microsoft Defender XDR',
    subtitle: 'security.microsoft.com Advanced Hunting',
    help: 'Queries Microsoft Defender XDR Advanced Hunting through Microsoft Graph /security/runHuntingQuery for MDE, email, identity, cloud app and alert tables.',
  },
};

function queryContainsQueryNowDirective(value: string): boolean {
  return /^\s*set\s+query_now\s*=\s*datetime\s*\(/im.test(value);
}

const ADVANCED_HUNTING_HISTORY_KEY = 'advanced-hunting-query-history:v1';

function readAdvancedHuntingHistory(): AdvancedHuntingHistoryItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(ADVANCED_HUNTING_HISTORY_KEY) || '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item): AdvancedHuntingHistoryItem | null => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const queryText = String(row.queryText ?? '').trim();
        if (!queryText) return null;
        return {
          id: String(row.id ?? `${Date.now()}`),
          title: String(row.title ?? queryText.split('\n')[0] ?? 'Advanced Hunting query'),
          source: row.source === 'defender' ? 'defender' : 'sentinel',
          customerId: String(row.customerId ?? ''),
          customerName: typeof row.customerName === 'string' ? row.customerName : undefined,
          queryText,
          queryExecutionMode: row.queryExecutionMode === 'set_query_now' || row.queryExecutionMode === 'custom' ? row.queryExecutionMode : 'standard',
          timeRangeMode: row.timeRangeMode === 'absolute' || row.timeRangeMode === 'in_query' ? row.timeRangeMode : 'lookback',
          timespan: typeof row.timespan === 'string' ? row.timespan : undefined,
          limit: Number(row.limit) > 0 ? Number(row.limit) : 500,
          rowCount: Number.isFinite(Number(row.rowCount)) ? Number(row.rowCount) : undefined,
          durationMs: Number.isFinite(Number(row.durationMs)) ? Number(row.durationMs) : undefined,
          ranAtUtc: typeof row.ranAtUtc === 'string' ? row.ranAtUtc : new Date().toISOString(),
        };
      })
      .filter((item): item is AdvancedHuntingHistoryItem => item !== null)
      .slice(0, 50);
  } catch {
    return [];
  }
}

function writeAdvancedHuntingHistory(items: AdvancedHuntingHistoryItem[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(ADVANCED_HUNTING_HISTORY_KEY, JSON.stringify(items.slice(0, 50)));
  } catch {
    // Ignore storage quota/private-mode failures.
  }
}

function parseLookbackTimespan(timespan?: string): { value: number; unit: LookbackUnit } | null {
  if (!timespan) return null;
  const hours = /^PT(\d+)H$/i.exec(timespan.trim());
  if (hours) return { value: Number(hours[1]), unit: 'hours' };
  const days = /^P(\d+)D$/i.exec(timespan.trim());
  if (days) return { value: Number(days[1]), unit: 'days' };
  return null;
}

function ListBlock({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-1">{title}</div>
      <ul className="text-sm text-foreground space-y-1 list-disc pl-5">
        {items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}
      </ul>
    </div>
  );
}

export function AdvancedHuntingTab({ fixedCustomerId, hideCustomerSelector = false, featureFlags = {}, pendingQuery = null, onPendingQueryConsumed }: AdvancedHuntingTabProps = {}) {
  const { customers, activeCustomerId, activeCustomer, setActiveCustomerId } = useCustomers();
  const { canCapability } = useAuth();
  const canRunQuery = canCapability('advanced_hunting', 'run_query');
  const hasSentinelSourceGate = canCapability('advanced_hunting', 'run_sentinel_query');
  const hasDefenderSourceGate = canCapability('advanced_hunting', 'run_defender_query');
  const hasAnySourceGate = hasSentinelSourceGate || hasDefenderSourceGate;
  const canRunSentinelQuery = canRunQuery && (!hasAnySourceGate || hasSentinelSourceGate);
  const canRunDefenderQuery = canRunQuery && (!hasAnySourceGate || hasDefenderSourceGate);
  const canUseQueryNow = canCapability('advanced_hunting', 'use_query_now');
  const canUseCustomQueryOptions = canCapability('advanced_hunting', 'custom_query_options');
  const canSetTimeRangeInQuery = canCapability('advanced_hunting', 'set_time_range_in_query');
  const canGenerateQuery = canCapability('advanced_hunting', 'generate_query');
  const canExportCsv = canCapability('advanced_hunting', 'export_csv');
  const canSaveQuery = canCapability('advanced_hunting', 'save_query');
  const canViewSavedQueries = canCapability('advanced_hunting', 'view_saved_queries');
  const canManageSavedQueries = canCapability('advanced_hunting', 'manage_saved_queries');
  const canFilterColumns = canCapability('advanced_hunting', 'filter_columns');
  const canResizeColumns = canCapability('advanced_hunting', 'resize_columns');
  const canUseQueryExplorer = canCapability('advanced_hunting', 'use_query_explorer');
  const canViewExplorerTables = canCapability('advanced_hunting', 'view_schema_tables');
  const canViewExplorerFunctions = canCapability('advanced_hunting', 'view_functions');
  const canViewSavedFunctions = canCapability('advanced_hunting', 'view_saved_functions');
  const canViewQueryHistory = canCapability('advanced_hunting', 'view_query_history');
  const canManageQueryHistory = canCapability('advanced_hunting', 'manage_query_history');
  const canSaveFunction = canCapability('advanced_hunting', 'save_function');
  const featureKqlIntellisense = featureFlags['advanced_hunting_kql_intellisense'] !== false;
  const featureRuntimeSchema = featureFlags['advanced_hunting_runtime_schema'] !== false;
  const featureQueryBuilder = featureFlags['advanced_hunting_query_builder'] !== false;
  const canUseKqlIntellisense = featureKqlIntellisense && canCapability('advanced_hunting', 'use_kql_intellisense');
  const canUseQueryBuilder = featureQueryBuilder && canCapability('advanced_hunting', 'use_query_builder');
  const canViewRuntimeSchema = featureRuntimeSchema && canCapability('advanced_hunting', 'view_runtime_schema');

  const initialCustomerId = fixedCustomerId || activeCustomerId || customers[0]?.id || '';
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(initialCustomerId);
  const [source, setSource] = useState<HuntingSource>('sentinel');
  const [queryExecutionMode, setQueryExecutionMode] = useState<QueryExecutionMode>('standard');
  const [query, setQuery] = useState<string>(SENTINEL_SAMPLE_QUERIES[0].query);

  useEffect(() => {
    const next = typeof pendingQuery === 'string' ? pendingQuery.trim() : '';
    if (!next) return;
    // Evidence pivots originate from Sentinel incidents, so force Sentinel mode
    // and place the query in the Advanced Hunting editor for analyst review.
    setSource('sentinel');
    setQuery(next);
    setQueryExecutionMode(queryContainsQueryNowDirective(next) ? 'set_query_now' : 'standard');
    setResult(null);
    setError(null);
    onPendingQueryConsumed?.();
  }, [pendingQuery, onPendingQueryConsumed]);
  const [limit, setLimit] = useState<number>(500);
  const [sentinelTimeMode, setSentinelTimeMode] = useState<SentinelTimeMode>('lookback');
  const [sentinelLookbackValue, setSentinelLookbackValue] = useState<number>(24);
  const [sentinelLookbackUnit, setSentinelLookbackUnit] = useState<SentinelLookbackUnit>('hours');
  const [sentinelFrom, setSentinelFrom] = useState<string>('');
  const [sentinelTo, setSentinelTo] = useState<string>('');
  const [defenderTimeMode, setDefenderTimeMode] = useState<TimeRangeMode>('lookback');
  const [defenderLookbackValue, setDefenderLookbackValue] = useState<number>(30);
  const [defenderLookbackUnit, setDefenderLookbackUnit] = useState<LookbackUnit>('days');
  const [defenderFrom, setDefenderFrom] = useState<string>('');
  const [defenderTo, setDefenderTo] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AdvancedHuntingResponse | null>(null);
  const [filteredResultRows, setFilteredResultRows] = useState<unknown[][] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [queryExplorerCollapsed, setQueryExplorerCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('advanced-hunting-explorer-collapsed') === 'true';
  });
  const [queryHistory, setQueryHistory] = useState<AdvancedHuntingHistoryItem[]>(() => readAdvancedHuntingHistory());

  const [saveQueryOpen, setSaveQueryOpen] = useState(false);
  const [saveQueryName, setSaveQueryName] = useState('');
  const [saveQueryDescription, setSaveQueryDescription] = useState('');
  const [saveQueryCategory, setSaveQueryCategory] = useState('');
  const [saveQueryLabels, setSaveQueryLabels] = useState('');
  const [saveQueryVisibility, setSaveQueryVisibility] = useState<'private' | 'shared' | 'global'>('shared');
  const [saveToDefaultQueryPack, setSaveToDefaultQueryPack] = useState(true);
  const [saveQueryLoading, setSaveQueryLoading] = useState(false);
  const [saveQueryError, setSaveQueryError] = useState<string | null>(null);
  const [saveQueryMessage, setSaveQueryMessage] = useState<string | null>(null);

  const [saveFunctionOpen, setSaveFunctionOpen] = useState(false);
  const [saveFunctionName, setSaveFunctionName] = useState('');
  const [saveFunctionDescription, setSaveFunctionDescription] = useState('');
  const [saveFunctionCategory, setSaveFunctionCategory] = useState('Reusable functions');
  const [saveFunctionLabels, setSaveFunctionLabels] = useState('');
  const [saveFunctionVisibility, setSaveFunctionVisibility] = useState<'private' | 'shared' | 'global'>('shared');
  const [saveFunctionLoading, setSaveFunctionLoading] = useState(false);
  const [saveFunctionError, setSaveFunctionError] = useState<string | null>(null);
  const [saveFunctionMessage, setSaveFunctionMessage] = useState<string | null>(null);

  const [aiPrompt, setAiPrompt] = useState<string>('Find potentially suspicious PowerShell activity in the last 7 days and explain what fields matter for triage.');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<AiQueryResponse | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [schemaTables, setSchemaTables] = useState<KqlTableDef[]>([]);
  const [schemaStatus, setSchemaStatus] = useState<string>('Static catalog');



  useEffect(() => {
    let cancelled = false;
    async function loadSchema() {
      if (!selectedCustomerId || !canViewRuntimeSchema) {
        setSchemaTables([]);
        setSchemaStatus(canViewRuntimeSchema ? 'Static catalog' : 'Static catalog · runtime schema disabled by permission');
        return;
      }
      try {
        setSchemaStatus('Loading workspace schema...');
        const data = await apiFetch<{ ok: boolean; tables?: KqlTableDef[]; source?: string; runtime?: boolean; error?: string }>(`/advanced-hunting/schema?customerId=${encodeURIComponent(selectedCustomerId)}&source=${encodeURIComponent(source)}`);
        if (cancelled) return;
        if (data.ok && Array.isArray(data.tables)) {
          setSchemaTables(data.tables);
          setSchemaStatus(data.runtime ? `Runtime schema · ${data.tables.length} tables` : `Static schema · ${data.tables.length} tables`);
        } else {
          setSchemaTables([]);
          setSchemaStatus(data.error ? `Static catalog · ${data.error}` : 'Static catalog');
        }
      } catch (e) {
        if (!cancelled) {
          setSchemaTables([]);
          setSchemaStatus(`Static catalog · ${String(e instanceof Error ? e.message : e).slice(0, 90)}`);
        }
      }
    }
    void loadSchema();
    return () => { cancelled = true; };
  }, [selectedCustomerId, source, canViewRuntimeSchema]);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId) || activeCustomer,
    [customers, selectedCustomerId, activeCustomer],
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('advanced-hunting-explorer-collapsed', String(queryExplorerCollapsed));
    }
  }, [queryExplorerCollapsed]);

  useEffect(() => {
    writeAdvancedHuntingHistory(queryHistory);
  }, [queryHistory]);

  function applyTimespanToControls(nextSource: HuntingSource, mode: TimeRangeMode | undefined, timespan?: string) {
    const nextMode = mode ?? 'lookback';
    if (nextSource === 'sentinel') {
      setSentinelTimeMode(nextMode);
      const parsed = parseLookbackTimespan(timespan);
      if (parsed) {
        setSentinelLookbackValue(parsed.value);
        setSentinelLookbackUnit(parsed.unit);
      }
      return;
    }

    setDefenderTimeMode(nextMode);
    const parsed = parseLookbackTimespan(timespan);
    if (parsed) {
      setDefenderLookbackValue(parsed.value);
      setDefenderLookbackUnit(parsed.unit);
    }
  }

  function loadQueryFromExplorer(payload: {
    queryText: string;
    source: HuntingSource;
    queryExecutionMode?: QueryExecutionMode;
    timeRangeMode?: TimeRangeMode;
    timespan?: string;
    limit?: number;
    name?: string;
  }) {
    setSource(payload.source);
    setQuery(payload.queryText);
    setQueryExecutionMode(payload.queryExecutionMode ?? (queryContainsQueryNowDirective(payload.queryText) ? 'set_query_now' : 'standard'));
    applyTimespanToControls(payload.source, payload.timeRangeMode, payload.timespan);
    if (payload.limit && Number.isFinite(payload.limit)) setLimit(Math.max(1, Math.min(5000, payload.limit)));
    setResult(null);
    setFilteredResultRows(null);
    setError(null);
  }

  function clearQueryHistory() {
    const confirmed = window.confirm('Clear Advanced Hunting query history stored in this browser?');
    if (confirmed) setQueryHistory([]);
  }

  function deleteQueryHistoryItem(id: string) {
    setQueryHistory((prev) => prev.filter((item) => item.id !== id));
  }

  const queryHasQueryNow = queryContainsQueryNowDirective(query);
  const canRunSelectedSource = source === 'defender' ? canRunDefenderQuery : canRunSentinelQuery;

  useEffect(() => {
    const nextCustomerId = fixedCustomerId || activeCustomerId || customers[0]?.id || '';
    if (nextCustomerId && nextCustomerId !== selectedCustomerId) {
      setSelectedCustomerId(nextCustomerId);
    }
  }, [fixedCustomerId, activeCustomerId, customers, selectedCustomerId]);

  useEffect(() => {
    if (source === 'defender' && queryExecutionMode === 'set_query_now') {
      setQueryExecutionMode('standard');
    }
  }, [source, queryExecutionMode]);

  useEffect(() => {
    if (source === 'sentinel' && queryHasQueryNow && queryExecutionMode === 'standard') {
      setQueryExecutionMode('set_query_now');
    }
  }, [source, queryHasQueryNow, queryExecutionMode]);

  const sampleQueries = source === 'defender' ? DEFENDER_SAMPLE_QUERIES : SENTINEL_SAMPLE_QUERIES;

  function toIsoFromDatetimeLocal(value: string): string | undefined {
    if (!value.trim()) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
  }

  function getSentinelTimespan(): string | undefined {
    if (sentinelTimeMode === 'in_query') return undefined;

    if (sentinelTimeMode === 'lookback') {
      const safeValue = Math.min(Math.max(Number(sentinelLookbackValue) || 24, 1), sentinelLookbackUnit === 'hours' ? 24 * 365 : 365);
      return sentinelLookbackUnit === 'hours' ? `PT${safeValue}H` : `P${safeValue}D`;
    }

    const fromIso = toIsoFromDatetimeLocal(sentinelFrom);
    const toIso = toIsoFromDatetimeLocal(sentinelTo);
    if (fromIso && toIso) return `${fromIso}/${toIso}`;
    if (fromIso) return `${fromIso}/${new Date().toISOString()}`;
    return undefined;
  }

  function describeSentinelTimespan(): string {
    if (sentinelTimeMode === 'in_query') return 'Set in Query (no external timespan)';
    const span = getSentinelTimespan();
    if (!span) return 'No explicit time range';
    if (sentinelTimeMode === 'lookback') {
      return `Last ${sentinelLookbackValue || 24} ${sentinelLookbackUnit}`;
    }
    return span;
  }

  function getDefenderTimespan(): string | undefined {
    if (defenderTimeMode === 'in_query') return undefined;

    if (defenderTimeMode === 'lookback') {
      const safeValue = Math.min(Math.max(Number(defenderLookbackValue) || 30, 1), defenderLookbackUnit === 'hours' ? 24 * 365 : 365);
      return defenderLookbackUnit === 'hours' ? `PT${safeValue}H` : `P${safeValue}D`;
    }

    const fromIso = toIsoFromDatetimeLocal(defenderFrom);
    const toIso = toIsoFromDatetimeLocal(defenderTo);
    if (fromIso && toIso) return `${fromIso}/${toIso}`;
    if (fromIso) return `${fromIso}/${new Date().toISOString()}`;
    return undefined;
  }

  function describeDefenderTimespan(): string {
    if (defenderTimeMode === 'in_query') return 'Set in Query (no external timespan)';
    const span = getDefenderTimespan();
    if (!span) return 'No explicit time range';
    if (defenderTimeMode === 'lookback') {
      return `Last ${defenderLookbackValue || 30} ${defenderLookbackUnit}`;
    }
    return span;
  }

  function applyRecommendedDefenderTimespan(recommendedTimespan?: string) {
    if (!recommendedTimespan) return;
    const trimmed = recommendedTimespan.trim();
    const days = /^P(\d+)D$/i.exec(trimmed);
    const hours = /^PT(\d+)H$/i.exec(trimmed);

    if (days) {
      setDefenderTimeMode('lookback');
      setDefenderLookbackUnit('days');
      setDefenderLookbackValue(Number(days[1]) || 30);
      return;
    }

    if (hours) {
      setDefenderTimeMode('lookback');
      setDefenderLookbackUnit('hours');
      setDefenderLookbackValue(Number(hours[1]) || 24);
      return;
    }

    if (trimmed.includes('/')) {
      const [fromRaw, toRaw] = trimmed.split('/', 2);
      const from = new Date(fromRaw);
      const to = new Date(toRaw);
      if (!Number.isNaN(from.getTime())) {
        setDefenderTimeMode('absolute');
        setDefenderFrom(from.toISOString().slice(0, 16));
      }
      if (!Number.isNaN(to.getTime())) {
        setDefenderTo(to.toISOString().slice(0, 16));
      }
    }
  }

  function changeSource(next: HuntingSource) {
    if (next === 'sentinel' && !canRunSentinelQuery) {
      setError('Your role is not allowed to run Sentinel / Log Analytics workspace queries.');
      return;
    }
    if (next === 'defender' && !canRunDefenderQuery) {
      setError('Your role is not allowed to run Microsoft Defender XDR Advanced Hunting queries.');
      return;
    }
    setSource(next);
    setQueryExecutionMode('standard');
    setQuery((next === 'defender' ? DEFENDER_SAMPLE_QUERIES : SENTINEL_SAMPLE_QUERIES)[0].query);
    setResult(null);
    setError(null);
    setAiResult(null);
    setAiError(null);
  }

  function recordAdvancedHuntingHistory(data: AdvancedHuntingResponse) {
    const queryText = query.trim();
    if (!queryText || !data.ok) return;
    const timespan = data.timespan ?? currentTimespan();
    const timeRangeMode = currentTimeRangeMode();
    const now = new Date().toISOString();
    const title = queryText.split('\n').map((line) => line.trim()).find(Boolean)?.slice(0, 90) || 'Advanced Hunting query';
    const item: AdvancedHuntingHistoryItem = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      title,
      source: data.source ?? source,
      customerId: selectedCustomerId,
      customerName: selectedCustomer?.name,
      queryText,
      queryExecutionMode,
      timeRangeMode,
      timespan,
      limit,
      rowCount: data.rowCount,
      durationMs: data.durationMs,
      ranAtUtc: now,
    };

    setQueryHistory((prev) => {
      const deduped = prev.filter((existing) => !(existing.source === item.source && existing.customerId === item.customerId && existing.queryText.trim() === item.queryText.trim()));
      return [item, ...deduped].slice(0, 50);
    });
  }

  function handleKqlEditorKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') return;

    // Match the analyst shortcuts used in Microsoft Sentinel / Log Analytics workbooks:
    // Ctrl+Enter continues a query pipeline on the next line, Shift+Enter runs the query.
    if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart ?? query.length;
      const end = textarea.selectionEnd ?? start;
      const insertion = '\n| ';
      const nextQuery = `${query.slice(0, start)}${insertion}${query.slice(end)}`;
      setQuery(nextQuery);

      window.setTimeout(() => {
        const caret = start + insertion.length;
        textarea.selectionStart = caret;
        textarea.selectionEnd = caret;
      }, 0);
      return;
    }

    if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      void runQuery();
    }
  }

  async function runQuery() {
    const customerId = selectedCustomerId || activeCustomerId;

    if (!canRunQuery) {
      setError('Your role is not allowed to run Advanced Hunting queries.');
      return;
    }

    if (!canRunSelectedSource) {
      setError(source === 'defender'
        ? 'Your role is not allowed to run Microsoft Defender XDR Advanced Hunting queries.'
        : 'Your role is not allowed to run Sentinel / Log Analytics workspace queries.');
      return;
    }

    if (source === 'defender' && queryHasQueryNow) {
      setError('Microsoft Defender XDR Advanced Hunting does not support `set query_now`. Switch to Sentinel / Log Analytics mode, or remove that line and use the Defender time-range dropdown.');
      return;
    }

    if (source === 'sentinel' && queryHasQueryNow && !canUseQueryNow) {
      setError('Your role is not allowed to use `set query_now = datetime(...)`. Enable Advanced Hunting / Use query_now in Permissions Master.');
      return;
    }

    if (queryExecutionMode === 'custom' && !canUseCustomQueryOptions) {
      setError('Your role is not allowed to use Advanced Hunting custom query options.');
      return;
    }

    const selectedTimeMode = source === 'defender' ? defenderTimeMode : sentinelTimeMode;
    if (selectedTimeMode === 'in_query' && !canSetTimeRangeInQuery) {
      setError('Your role is not allowed to set the Advanced Hunting time range inside the KQL query. Enable Advanced Hunting / Set Time Range in Query in Permissions Master.');
      return;
    }

    if (!customerId) {
      setError('Please select a customer/workspace.');
      return;
    }

    if (!query.trim()) {
      setError('Please enter a KQL query.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setFilteredResultRows(null);

    try {
      const data = await apiFetch<AdvancedHuntingResponse>('/advanced-hunting/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          source,
          query,
          limit,
          queryExecutionMode,
          timeRangeMode: source === 'defender' ? defenderTimeMode : sentinelTimeMode,
          allowQueryNow: source === 'sentinel' && (queryExecutionMode === 'set_query_now' || queryHasQueryNow),
          timespan: source === 'defender' ? getDefenderTimespan() : getSentinelTimespan(),
        }),
      });

      const dataWithSource = { ...data, source };
      setResult(dataWithSource);
      recordAdvancedHuntingHistory(dataWithSource);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('advanced-hunting-query-history-updated'));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function generateQueryWithAi() {
    const customerId = selectedCustomerId || activeCustomerId;

    if (!canGenerateQuery) {
      setAiError('Your role is not allowed to generate Advanced Hunting queries with AI.');
      return;
    }

    if (!customerId) {
      setAiError('Please select a customer/workspace.');
      return;
    }

    if (!aiPrompt.trim()) {
      setAiError('Describe the hunt you want the AI assistant to build.');
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiResult(null);

    try {
      const data = await apiFetch<AiQueryResponse>('/advanced-hunting/ai-query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          source,
          prompt: aiPrompt,
          currentQuery: query,
        }),
      });

      setAiResult(data);
      if (data.query) setQuery(data.query);
      if (source === 'defender' && data.recommendedTimespan) applyRecommendedDefenderTimespan(data.recommendedTimespan);
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiLoading(false);
    }
  }

  function applySample(sampleQuery: string) {
    setQuery(sampleQuery);
    setResult(null);
    setFilteredResultRows(null);
    setError(null);
  }

  function buildDefaultQueryName(): string {
    const firstMeaningfulLine = query
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line && !line.startsWith('//'));
    const base = firstMeaningfulLine || (source === 'defender' ? 'Defender Advanced Hunting Query' : 'Sentinel Log Analytics Query');
    return base.length > 80 ? `${base.slice(0, 77)}...` : base;
  }

  function openSaveQueryDialog() {
    setSaveQueryName((current) => current || buildDefaultQueryName());
    setSaveQueryDescription('');
    setSaveQueryCategory(source === 'defender' ? 'Defender XDR' : 'Log Analytics');
    setSaveQueryLabels('');
    setSaveQueryVisibility('shared');
    setSaveToDefaultQueryPack(true);
    setSaveQueryError(null);
    setSaveQueryMessage(null);
    setSaveQueryOpen(true);
  }

  function buildDefaultFunctionName(): string {
    const letMatch = /let\s+([A-Za-z_][A-Za-z0-9_]*)\s*=/.exec(query);
    if (letMatch?.[1]) return letMatch[1];
    const base = buildDefaultQueryName().replace(/[^a-z0-9_ -]+/gi, ' ').replace(/\s+/g, ' ').trim();
    return base ? `Function - ${base}`.slice(0, 90) : 'Reusable Advanced Hunting Function';
  }

  function openSaveFunctionDialog() {
    setSaveFunctionName((current) => current || buildDefaultFunctionName());
    setSaveFunctionDescription('');
    setSaveFunctionCategory(source === 'defender' ? 'Defender XDR' : 'Log Analytics');
    setSaveFunctionLabels('function, reusable');
    setSaveFunctionVisibility('shared');
    setSaveFunctionError(null);
    setSaveFunctionMessage(null);
    setSaveFunctionOpen(true);
  }

  function currentTimeRangeMode(): TimeRangeMode {
    return source === 'defender' ? defenderTimeMode : sentinelTimeMode;
  }

  function currentTimespan(): string | undefined {
    return source === 'defender' ? getDefenderTimespan() : getSentinelTimespan();
  }

  async function saveCurrentQuery() {
    const customerId = selectedCustomerId || activeCustomerId;

    if (!canSaveQuery) {
      setSaveQueryError('Your role is not allowed to save Advanced Hunting queries.');
      return;
    }

    if (!customerId) {
      setSaveQueryError('Please select a customer/workspace before saving this query.');
      return;
    }

    if (!saveQueryName.trim()) {
      setSaveQueryError('Query name is required.');
      return;
    }

    if (!query.trim()) {
      setSaveQueryError('The KQL query is empty.');
      return;
    }

    setSaveQueryLoading(true);
    setSaveQueryError(null);
    setSaveQueryMessage(null);

    try {
      await apiFetch<{ ok: boolean; id: string }>('/advanced-hunting/saved-queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          name: saveQueryName,
          description: saveQueryDescription,
          queryText: query,
          source,
          queryExecutionMode,
          timeRangeMode: currentTimeRangeMode(),
          timespan: currentTimespan(),
          resourceType: source === 'defender' ? 'Microsoft Defender XDR' : 'Log Analytics workspaces',
          category: saveQueryCategory,
          labels: saveQueryLabels
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          visibility: saveQueryVisibility,
          saveToDefaultQueryPack,
          metadata: {
            limit,
            customerName: selectedCustomer?.name,
            savedFrom: 'advanced_hunting',
          },
        }),
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('advanced-hunting-saved-query-created'));
      }
      setSaveQueryMessage('Query saved successfully. It is now available in Query Explorer → Saved Queries.');
    } catch (e) {
      setSaveQueryError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaveQueryLoading(false);
    }
  }

  async function saveCurrentFunction() {
    const customerId = selectedCustomerId || activeCustomerId;

    if (!canSaveFunction) {
      setSaveFunctionError('Your role is not allowed to save Advanced Hunting functions.');
      return;
    }

    if (!customerId) {
      setSaveFunctionError('Please select a customer/workspace before saving this function.');
      return;
    }

    if (!saveFunctionName.trim()) {
      setSaveFunctionError('Function name is required.');
      return;
    }

    if (!query.trim()) {
      setSaveFunctionError('The KQL/function text is empty.');
      return;
    }

    setSaveFunctionLoading(true);
    setSaveFunctionError(null);
    setSaveFunctionMessage(null);

    try {
      await apiFetch<{ ok: boolean; id: string }>('/advanced-hunting/functions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          name: saveFunctionName,
          description: saveFunctionDescription,
          functionText: query,
          source,
          queryExecutionMode,
          timeRangeMode: currentTimeRangeMode(),
          timespan: currentTimespan(),
          category: saveFunctionCategory,
          labels: saveFunctionLabels
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean),
          visibility: saveFunctionVisibility,
          metadata: {
            customerName: selectedCustomer?.name,
            savedFrom: 'advanced_hunting',
          },
        }),
      });

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('advanced-hunting-saved-function-created'));
      }
      setSaveFunctionMessage('Function saved successfully. It is now available in Query Explorer → Functions.');
    } catch (e) {
      setSaveFunctionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaveFunctionLoading(false);
    }
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4 items-start">
      {canUseQueryExplorer && (
        <AdvancedHuntingExplorer
          customerId={selectedCustomerId}
          customerName={selectedCustomer?.name}
          source={source}
          collapsed={queryExplorerCollapsed}
          onCollapsedChange={setQueryExplorerCollapsed}
          onLoadQuery={loadQueryFromExplorer}
          queryHistory={queryHistory}
          onClearHistory={clearQueryHistory}
          onDeleteHistoryItem={deleteQueryHistoryItem}
          canViewTables={canViewExplorerTables}
          canViewFunctions={canViewExplorerFunctions}
          canViewSavedFunctions={canViewSavedFunctions}
          canViewSavedQueries={canViewSavedQueries}
          canViewQueryHistory={canViewQueryHistory}
          canManageQueryHistory={canManageQueryHistory}
        />
      )}
      <div className="min-w-0 flex-1 slide-in space-y-4">
      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="max-w-xl">
            <h2 className="text-lg font-bold text-foreground">Advanced Hunting</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Run read-only KQL against Sentinel Log Analytics or Microsoft Defender XDR Advanced Hunting.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            {!hideCustomerSelector && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Customer</span>
                <select
                  className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm min-w-[260px]"
                  value={selectedCustomerId}
                  onChange={(e) => {
                    setSelectedCustomerId(e.target.value);
                    setActiveCustomerId(e.target.value);
                    setResult(null);
                    setError(null);
                  }}
                >
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Max rows</span>
              <input
                className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm w-28"
                type="number"
                min={1}
                max={5000}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value) || 500)}
                title="Maximum rows"
              />
            </div>

            {source === 'sentinel' && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Time range</span>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={sentinelTimeMode}
                    onChange={(e) => setSentinelTimeMode(e.target.value as SentinelTimeMode)}
                    title="Sentinel Log Analytics query time range"
                  >
                    <option value="lookback">Lookback</option>
                    <option value="absolute">From / To</option>
                    <option value="in_query" disabled={!canSetTimeRangeInQuery}>Set in Query</option>
                  </select>

                  {sentinelTimeMode === 'lookback' ? (
                    <>
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm w-24"
                        type="number"
                        min={1}
                        max={sentinelLookbackUnit === 'hours' ? 24 * 365 : 365}
                        value={sentinelLookbackValue}
                        onChange={(e) => setSentinelLookbackValue(Number(e.target.value) || 24)}
                        title="Sentinel query lookback value"
                      />
                      <select
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                        value={sentinelLookbackUnit}
                        onChange={(e) => setSentinelLookbackUnit(e.target.value as SentinelLookbackUnit)}
                        title="Sentinel query lookback unit"
                      >
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                    </>
                  ) : sentinelTimeMode === 'absolute' ? (
                    <>
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                        type="datetime-local"
                        value={sentinelFrom}
                        onChange={(e) => setSentinelFrom(e.target.value)}
                        title="Sentinel query start time"
                      />
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                        type="datetime-local"
                        value={sentinelTo}
                        onChange={(e) => setSentinelTo(e.target.value)}
                        title="Sentinel query end time"
                      />
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">Using the time filter written in KQL.</span>
                  )}
                </div>
              </div>
            )}

            {source === 'defender' && (
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Time range</span>
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={defenderTimeMode}
                    onChange={(e) => setDefenderTimeMode(e.target.value as TimeRangeMode)}
                    title="Microsoft Defender XDR Advanced Hunting time range"
                  >
                    <option value="lookback">Lookback</option>
                    <option value="absolute">From / To</option>
                    <option value="in_query" disabled={!canSetTimeRangeInQuery}>Set in Query</option>
                  </select>

                  {defenderTimeMode === 'lookback' ? (
                    <>
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm w-24"
                        type="number"
                        min={1}
                        max={defenderLookbackUnit === 'hours' ? 24 * 365 : 365}
                        value={defenderLookbackValue}
                        onChange={(e) => setDefenderLookbackValue(Number(e.target.value) || 30)}
                        title="Defender XDR query lookback value"
                      />
                      <select
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                        value={defenderLookbackUnit}
                        onChange={(e) => setDefenderLookbackUnit(e.target.value as LookbackUnit)}
                        title="Defender XDR query lookback unit"
                      >
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                      </select>
                    </>
                  ) : defenderTimeMode === 'absolute' ? (
                    <>
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                        type="datetime-local"
                        value={defenderFrom}
                        onChange={(e) => setDefenderFrom(e.target.value)}
                        title="Defender XDR query start time"
                      />
                      <input
                        className="border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                        type="datetime-local"
                        value={defenderTo}
                        onChange={(e) => setDefenderTo(e.target.value)}
                        title="Defender XDR query end time"
                      />
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">Using the time filter written in KQL.</span>
                  )}
                </div>
              </div>
            )}

            <div className="basis-full flex flex-wrap gap-2 pt-3 mt-1 border-t border-border">
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => void runQuery()}
                disabled={loading || !selectedCustomerId || !canRunQuery || !canRunSelectedSource}
                title={!canRunQuery ? 'Your role is not allowed to run Advanced Hunting queries' : (!canRunSelectedSource ? `Your role is not allowed to run ${SOURCE_META[source].label} queries` : undefined)}
              >
                {loading ? 'Running...' : source === 'defender' ? 'Run Defender Query' : 'Run Workspace Query'}
              </button>

              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={openSaveQueryDialog}
                disabled={!canSaveQuery || !query.trim() || !selectedCustomerId}
                title={!canSaveQuery ? 'Your role is not allowed to save Advanced Hunting queries' : 'Save the current KQL as a reusable query'}
              >
                Save as Query
              </button>

              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={openSaveFunctionDialog}
                disabled={!canSaveFunction || !query.trim() || !selectedCustomerId}
                title={!canSaveFunction ? 'Your role is not allowed to save Advanced Hunting functions' : 'Save the current KQL as a reusable function/snippet'}
              >
                Save as Function
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {(['sentinel', 'defender'] as HuntingSource[]).map((item) => {
            const active = source === item;
            const sourceAllowed = item === 'defender' ? canRunDefenderQuery : canRunSentinelQuery;
            return (
              <button
                key={item}
                type="button"
                onClick={() => changeSource(item)}
                disabled={!sourceAllowed}
                title={!sourceAllowed ? `Your role is not allowed to run ${SOURCE_META[item].label} queries.` : undefined}
                className={`text-left rounded-xl border p-4 transition disabled:opacity-50 disabled:cursor-not-allowed ${active ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' : 'border-border bg-muted/30 hover:bg-muted/60'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-foreground">{SOURCE_META[item].label}</div>
                  <span className={`text-[11px] font-bold rounded-full px-2 py-1 ${active ? 'bg-blue-600 text-white' : 'bg-background text-muted-foreground border border-border'}`}>
                    {SOURCE_META[item].subtitle}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{SOURCE_META[item].help}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {sampleQueries.map((sample) => (
            <button
              key={`${source}-${sample.label}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900 hover:bg-blue-100 dark:hover:bg-blue-900/60"
              onClick={() => applySample(sample.query)}
            >
              {sample.label}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3 items-start rounded-xl border border-border bg-muted/20 p-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Query mode / parameters</label>
            <select
              className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
              value={queryExecutionMode}
              onChange={(e) => setQueryExecutionMode(e.target.value as QueryExecutionMode)}
            >
              <option value="standard">Standard read-only</option>
              {source === 'sentinel' && <option value="set_query_now" disabled={!canUseQueryNow}>Set query_now replay</option>}
              <option value="custom" disabled={!canUseCustomQueryOptions}>Custom options</option>
            </select>
          </div>
          <div className="text-xs text-muted-foreground leading-relaxed">
            {source === 'sentinel' && queryExecutionMode === 'set_query_now' && (
              <span>Allows only the safe Sentinel scheduled-rule replay directive <code>set query_now = datetime(...)</code>. Other <code>set</code> directives and non-read-only KQL remain blocked.</span>
            )}
            {source === 'sentinel' && queryExecutionMode !== 'set_query_now' && (
              <span>Sentinel mode runs workspace KQL through Log Analytics. Choose <strong>Set query_now replay</strong> when pasting analytics-rule evidence KQL that contains <code>set query_now</code>.</span>
            )}
            {source === 'defender' && (
              <span>Defender mode uses Microsoft Graph Advanced Hunting. Graph does not support <code>set query_now</code>; use the Defender time-range controls instead.</span>
            )}
            {queryHasQueryNow && source === 'sentinel' && !canUseQueryNow && (
              <div className="mt-1 text-red-600 dark:text-red-400 font-semibold">Your role needs Advanced Hunting / Use query_now in Permissions Master.</div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-semibold text-muted-foreground mb-2">
            KQL Query {selectedCustomer ? `· ${selectedCustomer.name}` : ''} · {SOURCE_META[source].label}
          </label>
          <KqlIntellisenseEditor
            className="w-full min-h-[260px] border border-border bg-background text-foreground rounded-lg p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={query}
            onChange={setQuery}
            onKeyDown={handleKqlEditorKeyDown}
            source={source}
            customerName={selectedCustomer?.name}
            placeholder={source === 'defender' ? 'DeviceProcessEvents\n| where Timestamp > ago(7d)\n| take 50' : 'SecurityIncident\n| where TimeGenerated > ago(24h)\n| take 50'}
            schemaTables={canUseKqlIntellisense ? schemaTables : []}
            schemaStatus={canUseKqlIntellisense ? schemaStatus : 'IntelliSense gated by Permissions Master'}
          />
          <div className="mt-2 text-[11px] text-muted-foreground">
            Shortcuts: <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono">Enter</kbd> inserts a new pipeline line (<code>|</code>), and <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono">Shift</kbd> + <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted font-mono">Enter</kbd> runs the selected Sentinel/Defender query.
          </div>
        </div>

        {canUseQueryBuilder && (
          <div className="mt-4">
            <KustoForgeBuilder
              source={source}
              schemaTables={schemaTables}
              customerName={selectedCustomer?.name}
              onApplyQuery={(nextQuery) => { setQuery(nextQuery); setQueryExecutionMode(queryContainsQueryNowDirective(nextQuery) ? 'set_query_now' : 'standard'); }}
              disabled={loading}
            />
          </div>
        )}

        <div className="mt-3 text-xs text-muted-foreground space-y-1">
          <div>Backend safety controls still apply. Sentinel workspace mode enforces the KQL Table Access Master for non-Developer roles; Defender XDR mode uses Microsoft Graph Advanced Hunting and read-only query validation. Source execution and query-mode parameters are governed by Permissions Master capabilities.</div>
          {source === 'sentinel' && <div>Sentinel query time range: <span className="font-semibold">{describeSentinelTimespan()}</span>. {sentinelTimeMode === 'in_query' ? 'No external Log Analytics timespan will be sent.' : 'This is sent to Log Analytics as the query timespan and does not modify your KQL text.'}</div>}
          {source === 'defender' && <div>Defender XDR query time range: <span className="font-semibold">{describeDefenderTimespan()}</span>. {defenderTimeMode === 'in_query' ? 'No external Microsoft Graph timespan will be sent.' : 'This is sent to Microsoft Graph Advanced Hunting as the query timespan and does not modify your KQL text.'}</div>}
        </div>
      </div>

      <div className="bg-white dark:bg-card rounded-xl shadow p-5">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-bold text-foreground">AI KQL Assistant</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Describe the hunt you want. The assistant will generate KQL for the selected source and explain the logic before you run it.
            </p>
            <textarea
              className="mt-3 w-full min-h-[110px] border border-border bg-background text-foreground rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Example: Find devices that launched encoded PowerShell and then connected to rare external IPs in the last 7 days."
            />
          </div>
          <div className="lg:w-64 flex lg:flex-col gap-2">
            <button
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={() => void generateQueryWithAi()}
              disabled={aiLoading || !canGenerateQuery}
              title={!canGenerateQuery ? 'Your role is not allowed to generate Advanced Hunting queries with AI' : undefined}
            >
              {aiLoading ? 'Generating...' : 'Generate Query with AI'}
            </button>
            {aiResult?.query && (
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted"
                onClick={() => setQuery(aiResult.query)}
              >
                Re-apply AI Query
              </button>
            )}
          </div>
        </div>

        {aiError && (
          <div className="mt-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 rounded-xl p-4 text-sm">
            <strong>AI generation failed:</strong> {aiError}
          </div>
        )}

        {aiResult && (
          <div className="mt-4 border border-purple-100 dark:border-purple-900 bg-purple-50/60 dark:bg-purple-950/20 rounded-xl p-4 space-y-4">
            <div>
              <div className="text-sm font-bold text-foreground">Generated Query Explanation</div>
              <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{aiResult.explanation}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Source: {SOURCE_META[aiResult.source]?.label || aiResult.source}
                {aiResult.recommendedTimespan ? ` · Recommended timespan: ${aiResult.recommendedTimespan}` : ''}
                {aiResult.model ? ` · Model: ${aiResult.model}` : ''}
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <ListBlock title="Tables" items={aiResult.tables} />
              <ListBlock title="Assumptions" items={aiResult.assumptions} />
              <ListBlock title="Safety Notes" items={aiResult.safetyNotes} />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 rounded-xl p-4 text-sm">
          <strong>Query failed:</strong> {error}
        </div>
      )}


      {saveQueryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-card shadow-2xl border border-border">
            <div className="flex items-start justify-between gap-4 border-b border-border p-5">
              <div>
                <h3 className="text-lg font-bold text-foreground">Save as query</h3>
                <p className="text-sm text-muted-foreground mt-1">Save the current Advanced Hunting KQL with source and time-range metadata.</p>
              </div>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setSaveQueryOpen(false)} type="button">✕</button>
            </div>

            <div className="p-5 space-y-4">
              {saveQueryError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-3 text-sm">
                  {saveQueryError}
                </div>
              )}
              {saveQueryMessage && (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 p-3 text-sm">
                  {saveQueryMessage}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Query name *</label>
                <input
                  className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                  value={saveQueryName}
                  onChange={(event) => setSaveQueryName(event.target.value)}
                  placeholder="Type query name"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Description</label>
                <textarea
                  className="w-full min-h-[80px] border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                  value={saveQueryDescription}
                  onChange={(event) => setSaveQueryDescription(event.target.value)}
                  placeholder="Type query description"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={saveToDefaultQueryPack}
                  onChange={(event) => setSaveToDefaultQueryPack(event.target.checked)}
                />
                Save to the default query pack
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Resource type</label>
                  <input
                    className="w-full border border-border bg-muted text-foreground rounded-lg px-3 py-2 text-sm"
                    value={source === 'defender' ? 'Microsoft Defender XDR' : 'Log Analytics workspaces'}
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Visibility</label>
                  <select
                    className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={saveQueryVisibility}
                    onChange={(event) => setSaveQueryVisibility(event.target.value as 'private' | 'shared' | 'global')}
                  >
                    <option value="private">Private</option>
                    <option value="shared">Shared with customer</option>
                    <option value="global">Global</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Category</label>
                  <input
                    className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={saveQueryCategory}
                    onChange={(event) => setSaveQueryCategory(event.target.value)}
                    placeholder="Example: Incident triage"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Labels</label>
                  <input
                    className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={saveQueryLabels}
                    onChange={(event) => setSaveQueryLabels(event.target.value)}
                    placeholder="phishing, endpoint, identity"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                Source: <strong>{SOURCE_META[source].label}</strong> · Time mode: <strong>{currentTimeRangeMode()}</strong>{currentTimespan() ? <> · Timespan: <strong>{currentTimespan()}</strong></> : null}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-border p-5">
              <button className="px-4 py-2 rounded-lg text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted" onClick={() => setSaveQueryOpen(false)} type="button">Cancel</button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => void saveCurrentQuery()}
                disabled={saveQueryLoading || !saveQueryName.trim() || !query.trim()}
                type="button"
              >
                {saveQueryLoading ? 'Saving...' : 'Save Query'}
              </button>
            </div>
          </div>
        </div>
      )}

      {saveFunctionOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white dark:bg-card shadow-2xl border border-border">
            <div className="flex items-start justify-between gap-4 border-b border-border p-5">
              <div>
                <h3 className="text-lg font-bold text-foreground">Save as function</h3>
                <p className="text-sm text-muted-foreground mt-1">Save the current KQL as a reusable function/snippet. This action is audited.</p>
              </div>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setSaveFunctionOpen(false)} type="button">✕</button>
            </div>

            <div className="p-5 space-y-4">
              {saveFunctionError && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 p-3 text-sm">
                  {saveFunctionError}
                </div>
              )}
              {saveFunctionMessage && (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 p-3 text-sm">
                  {saveFunctionMessage}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Function name *</label>
                <input
                  className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                  value={saveFunctionName}
                  onChange={(event) => setSaveFunctionName(event.target.value)}
                  placeholder="Example: KS_NormalizeAccount"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Description</label>
                <textarea
                  className="w-full min-h-[80px] border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                  value={saveFunctionDescription}
                  onChange={(event) => setSaveFunctionDescription(event.target.value)}
                  placeholder="Describe what this function/snippet is used for"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Visibility</label>
                  <select
                    className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={saveFunctionVisibility}
                    onChange={(event) => setSaveFunctionVisibility(event.target.value as 'private' | 'shared' | 'global')}
                  >
                    <option value="private">Private</option>
                    <option value="shared">Shared with customer</option>
                    <option value="global">Global</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Category</label>
                  <input
                    className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={saveFunctionCategory}
                    onChange={(event) => setSaveFunctionCategory(event.target.value)}
                    placeholder="Example: Parsing helpers"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Labels</label>
                  <input
                    className="w-full border border-border bg-background text-foreground rounded-lg px-3 py-2 text-sm"
                    value={saveFunctionLabels}
                    onChange={(event) => setSaveFunctionLabels(event.target.value)}
                    placeholder="function, parsing, incident-triage"
                  />
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                Source: <strong>{SOURCE_META[source].label}</strong> · Time mode: <strong>{currentTimeRangeMode()}</strong>{currentTimespan() ? <> · Timespan: <strong>{currentTimespan()}</strong></> : null}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-border p-5">
              <button className="px-4 py-2 rounded-lg text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted" onClick={() => setSaveFunctionOpen(false)} type="button">Cancel</button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => void saveCurrentFunction()}
                disabled={saveFunctionLoading || !saveFunctionName.trim() || !query.trim()}
                type="button"
              >
                {saveFunctionLoading ? 'Saving...' : 'Save Function'}
              </button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white dark:bg-card rounded-xl shadow p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div>
              <h3 className="text-sm font-bold text-foreground">Query Results</h3>
              <p className="text-xs text-muted-foreground">
                Source: {result.source === 'defender' ? 'Microsoft Defender XDR' : 'Sentinel Log Analytics'} · Rows: {result.rowCount.toLocaleString()} · Duration: {result.durationMs} ms
                {result.workspaceId ? ` · Workspace: ${result.workspaceId}` : ''}
                {result.tenantId ? ` · Tenant: ${result.tenantId}` : ''}
                {result.timespan ? ` · Timespan: ${result.timespan}` : ''}
              </p>
            </div>

            {canExportCsv && (
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => exportAdvancedHuntingCsv({ ...result, rows: filteredResultRows ?? result.rows, rowCount: (filteredResultRows ?? result.rows).length }, selectedCustomer?.name)}
                disabled={!result || (filteredResultRows ?? result.rows).length === 0}
                title={(filteredResultRows ?? result.rows).length === 0 ? 'No rows to export' : 'Export currently visible query results to CSV'}
              >
                Export CSV
              </button>
            )}
          </div>

          {result.rows.length === 0 ? (
            <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center">
              Query completed successfully but returned no rows.
            </div>
          ) : (
            <AdvancedResultsGrid
              columns={result.columns}
              rows={result.rows}
              storageKey={`advanced-hunting-grid:${result.source ?? source}:${selectedCustomerId}:${result.columns.join('|')}`}
              canFilterColumns={canFilterColumns}
              canResizeColumns={canResizeColumns}
              onFilteredRowsChange={setFilteredResultRows}
            />
          )}
        </div>
      )}
      </div>
    </div>
  );
}
